import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { SuperAdminContext } from 'src/components';
import { useSuperAdminApiOverrides } from 'src/hooks/superAdmin';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import type { FlowRun } from 'src/models';
import { useMagicFlowEventsController } from './useMagicFlowEventsController';
import { useMagicFlowFlowRunsController } from './useMagicFlowFlowRunsController';
import { useMagicFlowFlowsController } from './useMagicFlowFlowsController';
import { useMagicFlowRecipesController } from './useMagicFlowRecipesController';

const tabFromPath = (pathname: string): string => {
  if (pathname.includes('/superadmin/magicflow/flow-runs')) return 'flow-runs';
  if (pathname.includes('/superadmin/magicflow/events')) return 'events';
  if (pathname.includes('/superadmin/magicflow/debugger')) return 'events';
  if (pathname.includes('/superadmin/magicflow/flows')) return 'flows';
  if (pathname.includes('/superadmin/magicflow/recipes')) return 'recipes';
  return 'flows';
};

const pathFromTab = (key: string): string => {
  if (key === 'flows') return '/superadmin/magicflow/flows';
  if (key === 'flow-runs') return '/superadmin/magicflow/flow-runs';
  if (key === 'events') return '/superadmin/magicflow/events';
  return '/superadmin/magicflow/recipes';
};

export const useMagicFlowAdminController = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const { org } = useContext(SuperAdminContext);
  const orgUuid = org?.org_uuid;
  const hasOrgSelected = Boolean(orgUuid);
  const { headers: superAdminHeaders } = useSuperAdminApiOverrides();

  const scopedHeaders = useMemo(() => {
    return {
      ...(superAdminHeaders || {}),
    } as Record<string, string | boolean>;
  }, [superAdminHeaders]);

  const recipeHeaders = useMemo(() => {
    const {
      'Impersonate-Org-UUID': _impersonatedOrg,
      'Headless-Mode': _headlessMode,
      ...rest
    } = scopedHeaders;
    return rest;
  }, [scopedHeaders]);

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname === '/superadmin/magicflow') {
      navigate('/superadmin/magicflow/flows', { replace: true });
    }
    if (location.pathname === '/superadmin/magicflow/debugger') {
      navigate('/superadmin/magicflow/events', { replace: true });
    }
  }, [location.pathname, navigate]);

  const recipesController = useMagicFlowRecipesController({
    scopedHeaders,
    recipeHeaders,
  });

  const flowsController = useMagicFlowFlowsController({
    orgUuid,
    hasOrgSelected,
    scopedHeaders,
  });

  const flowRunsController = useMagicFlowFlowRunsController({
    orgUuid,
    hasOrgSelected,
    scopedHeaders,
  });

  const eventsController = useMagicFlowEventsController({
    orgUuid,
    hasOrgSelected,
    scopedHeaders,
  });

  const [selectedFlowRun, setSelectedFlowRun] = useState<FlowRun | null>(null);
  const openFlowRunDetails = (run: FlowRun) => setSelectedFlowRun(run);
  const closeFlowRunDetails = () => setSelectedFlowRun(null);

  const activeTab = tabFromPath(location.pathname);

  return {
    orgUuid,
    hasOrgSelected,
    scopedHeaders,
    recipeHeaders,
    activeTab,
    onTabChange: (key: string) => navigate(pathFromTab(key)),
    selectedFlowRun,
    openFlowRunDetails,
    closeFlowRunDetails,
    ...recipesController,
    ...flowsController,
    ...flowRunsController,
    ...eventsController,
  };
};
